import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { obtenerDatosTenantPDF } from "./tenantPDF";

// ─── Paleta de colores ───────────────────────────────────────────────────────
const PALETTE = {
  contDark:   [15, 118, 110],   // #0F766E Teal 700
  contLight:  [45, 212, 191],   // #2DD4BF Teal 400
  contBanda:  [240, 253, 250],  // #F0FDFA Teal 50
  success:    [22, 163, 74],
  danger:     [220, 38, 38],
  dark:       [33, 37, 41],
  grayText:   [75, 85, 99],
  grayLabel:  [140, 145, 155],
  hairline:   [229, 231, 235],
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

const soloFecha = (f) => {
  if (!f) return "—";
  if (f instanceof Date) {
    const dd = String(f.getDate()).padStart(2, "0");
    const mm = String(f.getMonth() + 1).padStart(2, "0");
    const yy = f.getFullYear();
    return `${dd}/${mm}/${yy}`;
  }
  const s = String(f).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const dmy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) return `${dmy[1].padStart(2, "0")}/${dmy[2].padStart(2, "0")}/${dmy[3]}`;
  const d = new Date(s);
  if (!isNaN(d)) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = d.getFullYear();
    return `${dd}/${mm}/${yy}`;
  }
  return "—";
};

const procesarImagen = (src, width = 150, calidad = 1.0) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const escala = width / img.width;
      canvas.width = width;
      canvas.height = img.height * escala;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/png", calidad));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

function truncar(texto, max) {
  if (!texto) return "—";
  return texto.length > max ? texto.slice(0, max - 1) + "…" : texto;
}

function origenLabel(origen) {
  switch (origen) {
    case "automatico": return "Auto";
    case "recurrente": return "Recurrente";
    case "recepcion": return "Recepción";
    default: return "Manual";
  }
}

function parseMoney(str) {
  if (!str) return 0;
  const limpio = String(str).replace(/[$.\s]/g, "").replace(",", ".");
  return Number(limpio) || 0;
}

export async function generarPDFContable(movimientos, opciones = {}) {
  const doc = new jsPDF();
  const { desde, hasta, filtroCategoria, filtroOrigen } = opciones;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  let emp = { nombre: "Mi Empresa", direccion: "", telefono: "", logoUrl: null, fondoUrl: null };
  let logo = null, fondo = null;

  try {
    const datosTenant = await obtenerDatosTenantPDF();
    if (datosTenant) emp = datosTenant; 
    logo = emp.logoUrl ? await procesarImagen(emp.logoUrl, 250, 1.0) : null;
    fondo = emp.fondoUrl ? await procesarImagen(emp.fondoUrl, 300, 0.9) : null;
  } catch (error) {
    console.error("Error cargando recursos PDF:", error);
  }

  const insertarFondo = () => {
    if (!fondo) return;
    const cx = (pageW - 150) / 2, cy = (pageH - 150) / 2;
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.06 }));
    doc.addImage(fondo, "PNG", cx, cy, 150, 150);
    doc.restoreGraphicsState();
  };
  insertarFondo();

  const T = { dark: PALETTE.contDark, light: PALETTE.contLight, banda: PALETTE.contBanda };
  const MARGIN_X = 14;
  const MARGIN_TOP = 10;
  const rightMarginX = pageW - MARGIN_X;

  const bandaH = 26;
  doc.setFillColor(...T.banda);
  doc.rect(0, MARGIN_TOP, pageW, bandaH, "F");
  doc.setFillColor(...T.dark);
  doc.rect(0, MARGIN_TOP, pageW, 1.2, "F");

  if (logo) {
    const logoSize = 32;
    const logoX = MARGIN_X;
    const logoY = MARGIN_TOP + 4;
    doc.setFillColor(255, 255, 255);
    doc.circle(logoX + logoSize / 2, logoY + logoSize / 2, (logoSize / 2) + 1.5, "F");
    doc.addImage(logo, "PNG", logoX, logoY, logoSize, logoSize);
  }

  const infoX = MARGIN_X + 38;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(...PALETTE.dark);
  doc.text(emp.nombre || "", infoX, MARGIN_TOP + 11);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...PALETTE.grayText);
  doc.text(emp.direccion || "", infoX, MARGIN_TOP + 16);
  doc.text(emp.telefono ? `Cel-Whatsapp ${emp.telefono}` : "", infoX, MARGIN_TOP + 20.5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11.5);
  doc.setTextColor(100, 110, 120);
  doc.text("INFORME CONTABLE", rightMarginX, MARGIN_TOP + 13.5, { align: "right" });

  const periodoTextoInfo = desde && hasta
    ? `${soloFecha(desde)} - ${soloFecha(hasta)}`
    : desde ? `Desde ${soloFecha(desde)}`
    : hasta ? `Hasta ${soloFecha(hasta)}`
    : "Historial completo";

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...PALETTE.grayText);
  doc.text(`Período: ${periodoTextoInfo}`, rightMarginX, MARGIN_TOP + 18.5, { align: "right" });

  let yInfo = MARGIN_TOP + 35;
  if (filtroCategoria && filtroCategoria !== "todas") {
    doc.setFontSize(9);
    doc.text(`Categoría: ${filtroCategoria}`, rightMarginX, yInfo, { align: "right" });
    yInfo += 5;
  }
  if (filtroOrigen && filtroOrigen !== "todos") {
    doc.setFontSize(9);
    doc.text(`Origen: ${filtroOrigen}`, rightMarginX, yInfo, { align: "right" });
  }

  const applyHeaderGradient = (data, leftX = MARGIN_X, width = pageW - (MARGIN_X * 2)) => {
    if (data.section === "head") {
      const { x, y, width: cellW, height } = data.cell;
      const steps = Math.max(20, Math.ceil(cellW));
      const stripW = cellW / steps;
      for (let i = 0; i < steps; i++) {
        const localX = i * stripW;
        const globalX = x + localX - leftX;
        const t = Math.max(0, Math.min(1, globalX / width));
        const r = Math.round(T.dark[0] + t * (T.light[0] - T.dark[0]));
        const g = Math.round(T.dark[1] + t * (T.light[1] - T.dark[1]));
        const b = Math.round(T.dark[2] + t * (T.light[2] - T.dark[2]));
        doc.setFillColor(r, g, b);
        doc.rect(x + localX, y, stripW + 0.2, height, "F");
      }
    }
  };

  const commonTableStyles = {
    styles: { font: "helvetica", fontSize: 9.5, cellPadding: 1.5, textColor: PALETTE.dark },
    headStyles: { fillColor: false, textColor: 255, halign: "center", valign: "middle", fontStyle: "bold", fontSize: 9.5, minCellHeight: 9 },
    margin: { left: MARGIN_X, right: MARGIN_X },
    didDrawPage: insertarFondo,
  };

  const activos = movimientos.filter((m) => m.estado !== "eliminado");
  const ingresos = activos.filter((m) => m.tipo === "ingreso").reduce((s, m) => s + Number(m.monto || 0), 0);
  const gastos = activos.filter((m) => m.tipo === "gasto").reduce((s, m) => s + Number(m.monto || 0), 0);
  const balance = ingresos - gastos;

  const miniWidth = 140;
  const leftMarginSum = (pageW - miniWidth) / 2;
  autoTable(doc, {
    ...commonTableStyles,
    startY: MARGIN_TOP + 42,
    head: [["INGRESOS", "GASTOS", "BALANCE NETO"]],
    body: [[money(ingresos), money(gastos), money(balance)]],
    columnStyles: {
      0: { halign: "center", fontStyle: "bold", textColor: PALETTE.success, fontSize: 11 },
      1: { halign: "center", fontStyle: "bold", textColor: PALETTE.danger, fontSize: 11 },
      2: { halign: "center", fontStyle: "bold", textColor: balance >= 0 ? PALETTE.success : PALETTE.danger, fontSize: 11 },
    },
    tableWidth: miniWidth,
    margin: { left: leftMarginSum, right: leftMarginSum },
    didParseCell: (data) => { if (data.section === "body") data.cell.styles.fillColor = [255, 255, 255]; },
    willDrawCell: (data) => applyHeaderGradient(data, leftMarginSum, miniWidth),
  });

  let zebraIndex = 0;
  const tablaBody = activos.map((m) => [
    soloFecha(m.fecha),
    m.tipo === "ingreso" ? "INGRESO" : "GASTO",
    money(Math.abs(Number(m.monto || 0))),
    truncar(m.descripcion || m.categoria || "—", 50),
    m.categoria || "—",
    origenLabel(m.origen),
  ]);

  autoTable(doc, {
    ...commonTableStyles,
    startY: (doc.lastAutoTable?.finalY || 80) + 8,
    head: [["Fecha", "Tipo", "Monto", "Descripción", "Categoría", "Origen"]],
    body: tablaBody.length ? tablaBody : [["—", "—", "—", "Sin movimientos en este período", "—", "—"]],
    columnStyles: {
      0: { cellWidth: 22, halign: "center" },
      1: { cellWidth: 22, halign: "center" },
      2: { cellWidth: 26, halign: "right" },
      3: { cellWidth: 54 },
      4: { cellWidth: 35 },
      5: { cellWidth: 23, halign: "center" },
    },
    didParseCell: (data) => {
      const { section, column, cell, table, row } = data;
      if (section === "body") {
        const shade = zebraIndex % 2 === 0 ? 255 : 248;
        cell.styles.fillColor = [shade, shade, shade];
        if (column.index === 1) {
          cell.styles.textColor = cell.raw === "INGRESO" ? PALETTE.success : PALETTE.danger;
          cell.styles.fontStyle = "bold";
        }
        if (column.index === 2) cell.styles.textColor = row.raw?.[1] === "INGRESO" ? PALETTE.success : PALETTE.danger;
        if (column.index === table.columns.length - 1) zebraIndex++;
      }
    },
    willDrawCell: (data) => applyHeaderGradient(data),
  });

  const porCategoria = {};
  for (const m of activos) {
    const cat = m.categoria || "Sin categoría";
    if (!porCategoria[cat]) porCategoria[cat] = { ingresos: 0, gastos: 0, count: 0 };
    if (m.tipo === "ingreso") porCategoria[cat].ingresos += Number(m.monto || 0);
    if (m.tipo === "gasto") porCategoria[cat].gastos += Number(m.monto || 0);
    porCategoria[cat].count++;
  }

  const categoriasOrdenadas = Object.entries(porCategoria).sort((a, b) => (b[1].ingresos + b[1].gastos) - (a[1].ingresos + a[1].gastos));

  if (categoriasOrdenadas.length > 0) {
    let yActual = doc.lastAutoTable?.finalY || 200;
    if (yActual > pageH - 40) { doc.addPage(); insertarFondo(); yActual = 15; }

    const startYCat = yActual + 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...PALETTE.dark);
    doc.text("Desglose por categoría", MARGIN_X, startYCat);

    zebraIndex = 0;
    autoTable(doc, {
      ...commonTableStyles,
      startY: startYCat + 4,
      head: [["Categoría", "Movs", "Ingresos", "Gastos", "Neto"]],
      body: categoriasOrdenadas.map(([cat, v]) => [
        cat, v.count, money(v.ingresos), money(v.gastos), money(v.ingresos - v.gastos),
      ]),
      foot: [["TOTALES", activos.length, money(ingresos), money(gastos), money(balance)]],
      columnStyles: {
        0: { cellWidth: 58 },
        1: { cellWidth: 18, halign: "center" },
        2: { cellWidth: 35, halign: "right" },
        3: { cellWidth: 35, halign: "right" },
        4: { cellWidth: 36, halign: "right" },
      },
      didParseCell: (data) => {
        const { section, column, cell, table, row } = data;
        if (section === "body") {
          const shade = zebraIndex % 2 === 0 ? 255 : 248;
          cell.styles.fillColor = [shade, shade, shade];
          if (column.index === 2) cell.styles.textColor = PALETTE.success;
          if (column.index === 3) cell.styles.textColor = PALETTE.danger;
          if (column.index === 4) {
            const neto = (row.raw?.[2] ? parseMoney(row.raw[2]) : 0) - (row.raw?.[3] ? parseMoney(row.raw[3]) : 0);
            cell.styles.textColor = neto >= 0 ? PALETTE.success : PALETTE.danger;
            cell.styles.fontStyle = "bold";
          }
          if (column.index === table.columns.length - 1) zebraIndex++;
        }
        if (section === "foot") {
          cell.styles.fontStyle = "bold";
          cell.styles.fillColor = [240, 240, 240];
          if (column.index === 2) cell.styles.textColor = PALETTE.success;
          if (column.index === 3) cell.styles.textColor = PALETTE.danger;
          if (column.index === 4) cell.styles.textColor = balance >= 0 ? PALETTE.success : PALETTE.danger;
        }
      },
      willDrawCell: (data) => applyHeaderGradient(data),
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8.5);
    doc.setTextColor(...PALETTE.grayLabel);
    doc.text(`Página ${i} de ${totalPages}`, pageW / 2, pageH - 8, { align: "center" });
    doc.text(`Generado: ${new Date().toLocaleString("es-CO")}`, rightMarginX, pageH - 8, { align: "right" });
    doc.text(`Swalquiler - ${emp.nombre || ""}`, MARGIN_X, pageH - 8);
  }

  const fechaArchivo = new Date().toLocaleDateString("es-CO").replaceAll("/", "-");
  doc.save(`Contabilidad_${fechaArchivo}.pdf`);
}

export default generarPDFContable;